/**
 * useGuestProject Hook
 * Manages state and operations for guest project access
 * Uses local state updates to avoid full page reloads after mutations
 */
import { useState, useEffect, useCallback, useMemo } from 'react';
import { guestApi } from '../../../api/guestApi';
import { guestLinkRefusalFromError, GuestLinkRefusalError } from '../../../api/guestLinkRefusal';
import {
    ProjectViewData,
    WorkOrderViewData,
    GuestTaskRef,
    GuestWorkOrderState,
    GuestWorkOrderSource,
    BuildingContext,
    PhotoType,
    ConditionUpdatePayload,
    GuestComponentLocation,
} from '../../../types/guestAccess';
import { ProjectModel, ProjectNote, ProjectChecklist, ChecklistItem } from '../../../types/projects';
import { getComponentKeys } from '../../../types/workOrders';
import { PhotoModel } from '../../../types/protocols';
import { FileAttachment, ImageAttachment } from '../../../types/attachments';
import { formatDateForStorage } from '../../../Helpers/DateUtils';

/**
 * Per-section toggles for a work-order guest view (absent key = off).
 */
export type GuestEnabledSections = {
    notes?: boolean;
    checklists?: boolean;
    beforePictures?: boolean;
    progressPictures?: boolean;
    afterPictures?: boolean;
};

/**
 * Read-only work-order context shown above the completion form for a guest.
 */
export interface GuestWorkOrderContext {
    title: string;
    description?: string;
    priority?: string;
    dueDate?: string;
    /** Drives the read-only completed view. */
    status: GuestWorkOrderState;
    /** Where the work order came from (felanmälan, underhåll, manuell). */
    source?: GuestWorkOrderSource;
    /** Unit snapshot label ("Lägenhet 1203"), when linked to a unit. */
    unitLabel?: string;
    /** Parent project title, when the work order is a delmoment. */
    projectTitle?: string;
    createdAt?: string;
    /** When completed (archived delmoment) */
    completedAt?: string;
    /** Completion comment recorded at completion */
    completionComment?: string;
    /** Condition updates recorded at completion, keyed by componentKey. */
    completionConditions?: Record<string, { value: number; comment?: string }>;
    /** Linked component keys (normalized; legacy componentKey included) */
    componentKeys: string[];
    /** Display names for linked components, keyed by componentKey */
    componentNames: Record<string, string>;
    /** Resolved location metadata (photo/blueprint/map markers + GPS), keyed by componentKey */
    componentLocations: Record<string, GuestComponentLocation>;
    requiresPhoto: boolean;
    requiresComment: boolean;
    requiresConditionUpdate: boolean;
    /** Read-only source photos from the originating issue. */
    sourcePhotos?: { [key: string]: PhotoModel };
    /** Read-only attachments. */
    attachments?: { [key: string]: unknown };
    enabledSections: GuestEnabledSections;
}

/**
 * Initial data accepted by the hook — either a project or a work-order view.
 * The discriminant `taskType` selects parsing and the terminal completion action.
 */
export type GuestTaskInitialData = ProjectViewData | WorkOrderViewData;

/**
 * Validate and parse the granular-data object (project OR work order) from the
 * API response. Both share the granular sub-structures (notes/checklists/
 * before|progress|afterPictures), so the project-shaped model is reused.
 */
function parseGranularData(data: Record<string, unknown>): ProjectModel {
    // Validate required fields exist
    if (typeof data.key !== 'string' || !data.key) {
        throw new Error('Datan saknar nyckel');
    }
    if (typeof data.buildingKey !== 'string' || !data.buildingKey) {
        throw new Error('Datan saknar byggnadsnyckel');
    }
    if (typeof data.title !== 'string') {
        throw new Error('Datan saknar titel');
    }

    // Data has required fields, safe to cast (granular fields read by sections)
    return data as unknown as ProjectModel;
}

/**
 * Extract the read-only work-order context for the guest header.
 */
function parseWorkOrderContext(data: WorkOrderViewData): GuestWorkOrderContext {
    const wo = data.workOrder as Record<string, unknown>;
    return {
        title: (wo.title as string) || 'Ärende',
        description: wo.description as string | undefined,
        priority: wo.priority as string | undefined,
        dueDate: wo.dueDate as string | undefined,
        status: data.state ?? ((wo.status as GuestWorkOrderState) || 'open'),
        source: data.source,
        unitLabel: typeof wo.unitLabel === 'string' ? wo.unitLabel : undefined,
        projectTitle: typeof wo.projectTitle === 'string' ? wo.projectTitle : undefined,
        createdAt: typeof wo.createdAt === 'string' ? wo.createdAt : undefined,
        completedAt: wo.completedAt as string | undefined,
        completionComment: wo.completionComment as string | undefined,
        completionConditions: wo.completionConditions as GuestWorkOrderContext['completionConditions'],
        componentKeys: getComponentKeys(wo as { componentKeys?: string[] }),
        componentNames: data.componentNames || {},
        componentLocations: data.componentLocations || {},
        requiresPhoto: (wo.requiresPhoto as boolean) ?? false,
        requiresComment: (wo.requiresComment as boolean) ?? false,
        requiresConditionUpdate: (wo.requiresConditionUpdate as boolean) ?? false,
        sourcePhotos: wo.photos as { [key: string]: PhotoModel } | undefined,
        attachments: wo.attachments as { [key: string]: unknown } | undefined,
        enabledSections: (data.enabledSections as GuestEnabledSections) || {},
    };
}

interface UseGuestProjectReturn {
    // State
    loading: boolean;
    error: string | null;
    /** Set when a call was refused because of the link's state; the page renders the parked page. */
    refusal: GuestLinkRefusalError | null;
    /** Task type discriminant (project vs work order). */
    taskType: 'project' | 'workOrder';
    project: ProjectModel | null;
    building: BuildingContext | null;
    externalUserId: string | null;
    submitting: boolean;
    uploadingPhotoType: PhotoType | null;
    /** When external user signaled completion (project flow) */
    completedAt: string | null;
    /** Completion note from external user (project flow) */
    completedNote: string | null;

    // Work-order-only context (null for projects)
    workOrderContext: GuestWorkOrderContext | null;
    /** True once a work order has been completed via completeWorkOrder. */
    workOrderCompleted: boolean;

    // Actions
    refetch: () => Promise<void>;
    clearError: () => void;

    // Completion (project flow)
    setCompletion: (completed: boolean, note?: string) => Promise<void>;

    // Completion (work-order flow). After-photos are the completion evidence and
    // are added via the Efter-bilder section (uploadPhoto, photoType 'after')
    // before this terminal call — completeWorkOrder no longer uploads photos.
    completeWorkOrder: (
        comment?: string,
        newConditions?: { [componentKey: string]: ConditionUpdatePayload }
    ) => Promise<void>;

    // Notes
    addNote: (title: string, content: string) => Promise<void>;
    updateNote: (noteKey: string, title: string, content: string) => Promise<void>;
    deleteNote: (noteKey: string) => Promise<void>;

    // Checklists
    createChecklist: (title: string, items?: string[]) => Promise<void>;
    addChecklistItem: (checklistKey: string, text: string) => Promise<void>;
    setChecklistItemCompleted: (checklistKey: string, itemKey: string, completed: boolean) => Promise<void>;
    deleteChecklistItem: (checklistKey: string, itemKey: string) => Promise<void>;
    deleteChecklist: (checklistKey: string) => Promise<void>;

    // Photos
    uploadPhoto: (file: File, photoType: PhotoType) => Promise<void>;
    deletePhoto: (photoType: PhotoType, key: string) => Promise<void>;

    // Attachments
    uploadAttachment: (file: File) => Promise<void>;
    deleteAttachment: (attachmentKey: string) => Promise<void>;
    getDownloadUrl: (storagePath: string) => Promise<string>;

    // Helpers
    canDelete: (authorId: string) => boolean;
}

export const useGuestProject = (
    token: string | undefined,
    initialData: GuestTaskInitialData | undefined,
    /**
     * The task this hook drives, threaded into every guest API call: the link
     * identifies the user, the key names the work order or project.
     */
    taskRef: GuestTaskRef
): UseGuestProjectReturn => {
    const { workOrderKey, projectKey } = taskRef;
    const task = useMemo<GuestTaskRef>(
        () => (workOrderKey ? { workOrderKey } : { projectKey: projectKey as string }),
        [workOrderKey, projectKey]
    );
    const isWorkOrder = !!workOrderKey;

    // Granular-data source: project node or work-order node (both share fields).
    const parseInitial = (d?: GuestTaskInitialData): ProjectModel | null => {
        if (!d) return null;
        return d.taskType === 'workOrder'
            ? parseGranularData(d.workOrder)
            : parseGranularData(d.project);
    };

    // If initialData is provided, use it directly (pre-loaded by the route)
    const [loading, setLoading] = useState(!initialData);
    const [error, setError] = useState<string | null>(null);
    const [refusal, setRefusal] = useState<GuestLinkRefusalError | null>(null);
    const [taskType] = useState<'project' | 'workOrder'>(isWorkOrder ? 'workOrder' : 'project');
    const [project, setProject] = useState<ProjectModel | null>(parseInitial(initialData));
    const [building, setBuilding] = useState<BuildingContext | null>(
        initialData?.building ?? null
    );
    const [externalUserId, setExternalUserId] = useState<string | null>(
        initialData?.externalUserId ?? null
    );
    const [completedAt, setCompletedAt] = useState<string | null>(
        initialData && initialData.taskType === 'project' ? initialData.completedAt ?? null : null
    );
    const [completedNote, setCompletedNote] = useState<string | null>(
        initialData && initialData.taskType === 'project' ? initialData.completedNote ?? null : null
    );
    const [workOrderContext, setWorkOrderContext] = useState<GuestWorkOrderContext | null>(
        initialData && initialData.taskType === 'workOrder'
            ? parseWorkOrderContext(initialData)
            : null
    );
    const [workOrderCompleted, setWorkOrderCompleted] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [uploadingPhotoType, setUploadingPhotoType] = useState<PhotoType | null>(null);

    // Check if current user can delete an item
    const canDelete = useCallback((authorId: string): boolean => {
        return authorId === externalUserId;
    }, [externalUserId]);

    // A refusal mid-session (the link expired, was replaced or revoked while
    // the page was open) parks the page like a refused load; anything else is
    // shown as the message.
    const parkOnRefusal = useCallback((err: unknown): boolean => {
        const refused = guestLinkRefusalFromError(err);
        if (refused) setRefusal(refused);
        return refused !== null;
    }, []);
    const fail = useCallback((err: unknown, message: string) => {
        if (!parkOnRefusal(err)) setError(message);
    }, [parkOnRefusal]);

    // Fetch data (only used if no initialData provided). Branches on task type.
    const fetchProject = useCallback(async () => {
        if (!token) {
            setError('Ingen åtkomsttoken tillhandahållen');
            setLoading(false);
            return;
        }

        setLoading(true);
        setError(null);

        try {
            if (task.workOrderKey) {
                const data = await guestApi.getWorkOrder(token, task.workOrderKey);
                setProject(parseGranularData(data.workOrder));
                setBuilding(data.building);
                setExternalUserId(data.externalUserId);
                setWorkOrderContext(parseWorkOrderContext(data));
            } else if (task.projectKey) {
                const data = await guestApi.getProject(token, task.projectKey);
                setProject(parseGranularData(data.project));
                setBuilding(data.building);
                setExternalUserId(data.externalUserId);
                setCompletedAt(data.completedAt ?? null);
                setCompletedNote(data.completedNote ?? null);
            }
        } catch (err) {
            console.error('Error fetching guest task:', err);
            const errorMessage = err instanceof Error ? err.message : 'Kunde inte ladda uppgiften';
            fail(err, errorMessage);
        } finally {
            setLoading(false);
        }
    }, [token, task, fail]);

    // Initial fetch only if no initialData
    useEffect(() => {
        if (!initialData) {
            fetchProject();
        }
    }, [fetchProject, initialData]);

    const clearError = useCallback(() => setError(null), []);

    // ============================================================================
    // Completion Signal
    // ============================================================================

    const setCompletion = useCallback(async (completed: boolean, note?: string) => {
        if (!token || !task.projectKey) {
            setError('Sessionen är inte aktiv');
            return;
        }
        setSubmitting(true);
        setError(null);

        try {
            await guestApi.setProjectCompletion(token, task.projectKey, completed, note);

            if (completed) {
                // Update local state with completion
                setCompletedAt(formatDateForStorage(new Date()));
                setCompletedNote(note?.trim() || null);
            } else {
                // Clear completion
                setCompletedAt(null);
                setCompletedNote(null);
            }
        } catch (err) {
            console.error('Error setting completion:', err);
            // Surface the server message when available (e.g. the work-order gate
            // "Alla ärenden måste vara slutförda...") so the guest sees why.
            const fallback = completed ? 'Kunde inte markera som klar' : 'Kunde inte ångra markering';
            fail(err, err instanceof Error && err.message ? err.message : fallback);
            throw err; // Re-throw so component can handle
        } finally {
            setSubmitting(false);
        }
    }, [token, task, fail]);

    // ============================================================================
    // Work-order completion (terminal "Slutför")
    // ============================================================================

    const completeWorkOrder = useCallback(async (
        comment?: string,
        newConditions?: { [componentKey: string]: ConditionUpdatePayload }
    ) => {
        if (!token || !task.workOrderKey) {
            setError('Sessionen är inte aktiv');
            throw new Error('Sessionen är inte aktiv');
        }
        setSubmitting(true);
        setError(null);

        try {
            // After-photos (completion evidence) are added separately via the
            // Efter-bilder section (uploadPhoto, photoType 'after'). The server
            // gates completion on afterPictures, so no photos are uploaded here.
            // Terminal completion: archives the work order; the personal link
            // stays valid and the order moves to "Nyligen slutförda".
            await guestApi.completeWorkOrder(token, {
                comment: comment?.trim() || undefined,
                newConditions,
            }, task.workOrderKey);

            setWorkOrderCompleted(true);
        } catch (err) {
            console.error('Error completing work order:', err);
            const message = err instanceof Error ? err.message : 'Kunde inte slutföra ärendet';
            fail(err, message);
            throw err;
        } finally {
            setSubmitting(false);
        }
    }, [token, task, fail]);

    // ============================================================================
    // Notes
    // ============================================================================

    const addNote = useCallback(async (title: string, content: string) => {
        if (!token || !externalUserId) return;
        setSubmitting(true);
        setError(null);

        try {
            const noteKey = await guestApi.addNote(token, task, title, content);

            // Update local state
            setProject(prev => {
                if (!prev) return prev;
                const newNote: ProjectNote = {
                    key: noteKey,
                    title: title.trim(),
                    content: content.trim(),
                    createdBy: externalUserId,
                    createdAt: formatDateForStorage(new Date()),
                };
                return {
                    ...prev,
                    notes: {
                        ...prev.notes,
                        [noteKey]: newNote,
                    },
                };
            });
        } catch (err) {
            console.error('Error adding note:', err);
            fail(err, 'Kunde inte lägga till anteckning');
        } finally {
            setSubmitting(false);
        }
    }, [token, externalUserId, task, fail]);

    const updateNote = useCallback(async (noteKey: string, title: string, content: string) => {
        if (!token || !externalUserId) return;
        setSubmitting(true);
        setError(null);

        try {
            await guestApi.updateNote(token, task, noteKey, title, content);

            // Update local state
            setProject(prev => {
                if (!prev || !prev.notes?.[noteKey]) return prev;
                return {
                    ...prev,
                    notes: {
                        ...prev.notes,
                        [noteKey]: {
                            ...prev.notes[noteKey],
                            title: title.trim(),
                            content: content.trim(),
                            updatedBy: externalUserId,
                            updatedAt: formatDateForStorage(new Date()),
                        },
                    },
                };
            });
        } catch (err) {
            console.error('Error updating note:', err);
            fail(err, 'Kunde inte uppdatera anteckning');
        } finally {
            setSubmitting(false);
        }
    }, [token, externalUserId, task, fail]);

    const deleteNote = useCallback(async (noteKey: string) => {
        if (!token) {
            setError('Sessionen är inte aktiv');
            return;
        }
        setSubmitting(true);
        setError(null);

        try {
            await guestApi.deleteNote(token, task, noteKey);

            // Update local state
            setProject(prev => {
                if (!prev || !prev.notes) return prev;
                const { [noteKey]: _, ...remainingNotes } = prev.notes;
                return {
                    ...prev,
                    notes: remainingNotes,
                };
            });
        } catch (err) {
            console.error('Error deleting note:', err);
            fail(err, 'Kunde inte ta bort anteckning');
        } finally {
            setSubmitting(false);
        }
    }, [token, task, fail]);

    // ============================================================================
    // Checklists
    // ============================================================================

    const createChecklist = useCallback(async (title: string, items?: string[]) => {
        if (!token || !externalUserId) return;
        setSubmitting(true);
        setError(null);

        try {
            const checklistKey = await guestApi.createChecklist(token, task, title, items);
            const now = formatDateForStorage(new Date());

            // Build items object if provided
            const itemsObj: Record<string, ChecklistItem> = {};
            if (items) {
                items.forEach((text, index) => {
                    if (text.trim()) {
                        const itemKey = `temp_${index}_${Date.now()}`;
                        itemsObj[itemKey] = {
                            key: itemKey,
                            text: text.trim(),
                            completed: false,
                            sortOrder: index,
                        };
                    }
                });
            }

            // Update local state
            setProject(prev => {
                if (!prev) return prev;
                const newChecklist: ProjectChecklist = {
                    key: checklistKey,
                    title: title.trim(),
                    createdBy: externalUserId,
                    createdAt: now,
                    items: itemsObj,
                };
                return {
                    ...prev,
                    checklists: {
                        ...prev.checklists,
                        [checklistKey]: newChecklist,
                    },
                };
            });
        } catch (err) {
            console.error('Error creating checklist:', err);
            fail(err, 'Kunde inte skapa checklista');
        } finally {
            setSubmitting(false);
        }
    }, [token, externalUserId, task, fail]);

    const addChecklistItem = useCallback(async (checklistKey: string, text: string) => {
        if (!token) {
            setError('Sessionen är inte aktiv');
            return;
        }
        setSubmitting(true);
        setError(null);

        try {
            const itemKey = await guestApi.addChecklistItem(token, task, checklistKey, text);

            // Update local state
            setProject(prev => {
                if (!prev || !prev.checklists?.[checklistKey]) return prev;
                const checklist = prev.checklists[checklistKey];
                const existingItems = checklist.items || {};
                const maxSortOrder = Object.values(existingItems).reduce(
                    (max, item) => Math.max(max, item.sortOrder || 0),
                    -1
                );

                const newItem: ChecklistItem = {
                    key: itemKey,
                    text: text.trim(),
                    completed: false,
                    sortOrder: maxSortOrder + 1,
                };

                return {
                    ...prev,
                    checklists: {
                        ...prev.checklists,
                        [checklistKey]: {
                            ...checklist,
                            items: {
                                ...existingItems,
                                [itemKey]: newItem,
                            },
                        },
                    },
                };
            });
        } catch (err) {
            console.error('Error adding checklist item:', err);
            fail(err, 'Kunde inte lägga till punkt');
        } finally {
            setSubmitting(false);
        }
    }, [token, task, fail]);

    const setChecklistItemCompleted = useCallback(async (
        checklistKey: string,
        itemKey: string,
        completed: boolean
    ) => {
        if (!token || !externalUserId) return;
        setSubmitting(true);
        setError(null);

        try {
            await guestApi.setChecklistItemCompleted(token, task, checklistKey, itemKey, completed);
            const now = formatDateForStorage(new Date());

            // Update local state
            setProject(prev => {
                if (!prev || !prev.checklists?.[checklistKey]?.items?.[itemKey]) return prev;
                const checklist = prev.checklists[checklistKey];
                const item = checklist.items![itemKey];

                return {
                    ...prev,
                    checklists: {
                        ...prev.checklists,
                        [checklistKey]: {
                            ...checklist,
                            items: {
                                ...checklist.items,
                                [itemKey]: {
                                    ...item,
                                    completed,
                                    completedAt: completed ? now : undefined,
                                    completedBy: completed ? externalUserId : undefined,
                                },
                            },
                        },
                    },
                };
            });
        } catch (err) {
            console.error('Error updating checklist item:', err);
            fail(err, 'Kunde inte uppdatera punkt');
        } finally {
            setSubmitting(false);
        }
    }, [token, externalUserId, task, fail]);

    const deleteChecklistItem = useCallback(async (checklistKey: string, itemKey: string) => {
        if (!token) {
            setError('Sessionen är inte aktiv');
            return;
        }
        setSubmitting(true);
        setError(null);

        try {
            await guestApi.deleteChecklistItem(token, task, checklistKey, itemKey);

            // Update local state
            setProject(prev => {
                if (!prev || !prev.checklists?.[checklistKey]?.items) return prev;
                const checklist = prev.checklists[checklistKey];
                const { [itemKey]: _, ...remainingItems } = checklist.items!;

                return {
                    ...prev,
                    checklists: {
                        ...prev.checklists,
                        [checklistKey]: {
                            ...checklist,
                            items: remainingItems,
                        },
                    },
                };
            });
        } catch (err) {
            console.error('Error deleting checklist item:', err);
            fail(err, 'Kunde inte ta bort punkt');
        } finally {
            setSubmitting(false);
        }
    }, [token, task, fail]);

    const deleteChecklist = useCallback(async (checklistKey: string) => {
        if (!token) {
            setError('Sessionen är inte aktiv');
            return;
        }
        setSubmitting(true);
        setError(null);

        try {
            await guestApi.deleteChecklist(token, task, checklistKey);

            // Update local state
            setProject(prev => {
                if (!prev || !prev.checklists) return prev;
                const { [checklistKey]: _, ...remainingChecklists } = prev.checklists;
                return {
                    ...prev,
                    checklists: remainingChecklists,
                };
            });
        } catch (err) {
            console.error('Error deleting checklist:', err);
            fail(err, 'Kunde inte ta bort checklista');
        } finally {
            setSubmitting(false);
        }
    }, [token, task, fail]);

    // ============================================================================
    // Photos
    // ============================================================================

    const getPhotoFieldName = (photoType: PhotoType): 'beforePictures' | 'progressPictures' | 'afterPictures' => {
        const map = {
            before: 'beforePictures',
            progress: 'progressPictures',
            after: 'afterPictures',
        } as const;
        return map[photoType];
    };

    const uploadPhoto = useCallback(async (file: File, photoType: PhotoType) => {
        if (!token || !externalUserId) return;
        setSubmitting(true);
        setUploadingPhotoType(photoType);
        setError(null);

        try {
            // Get upload URL and storage path from server
            const { uploadUrl, key, storagePath } = await guestApi.getUploadUrl(token, task, file.name, file.type, photoType);

            // Upload file directly to signed URL
            const uploadResponse = await fetch(uploadUrl, {
                method: 'PUT',
                body: file,
                headers: {
                    'Content-Type': file.type,
                },
            });

            if (!uploadResponse.ok) {
                const errorText = await uploadResponse.text();
                console.error('Storage upload failed:', uploadResponse.status, errorText);
                throw new Error(`Upload failed: ${uploadResponse.status}`);
            }

            // Get image dimensions
            const dimensions = await getImageDimensions(file);

            // Register upload with server
            await guestApi.registerUpload(token, task, {
                key,
                storagePath,
                width: dimensions.width,
                height: dimensions.height,
                fileSize: file.size,
                photoType,
            });

            // Update local state
            const fieldName = getPhotoFieldName(photoType);
            const now = formatDateForStorage(new Date());

            setProject(prev => {
                if (!prev) return prev;
                const existingPhotos = prev[fieldName] || {};
                return {
                    ...prev,
                    [fieldName]: {
                        ...existingPhotos,
                        [key]: {
                            key,
                            storagePath,
                            width: dimensions.width,
                            height: dimensions.height,
                            fileSize: file.size,
                            author: externalUserId,
                            date: now,
                        },
                    },
                };
            });
        } catch (err) {
            console.error('Error uploading photo:', err);
            parkOnRefusal(err);
            throw err; // Re-throw so component can handle locally
        } finally {
            setSubmitting(false);
            setUploadingPhotoType(null);
        }
    }, [token, externalUserId, task, parkOnRefusal]);

    const deletePhoto = useCallback(async (photoType: PhotoType, key: string) => {
        if (!token) {
            setError('Sessionen är inte aktiv');
            return;
        }
        setSubmitting(true);
        setError(null);

        try {
            await guestApi.deletePhoto(token, task, photoType, key);

            // Update local state
            const fieldName = getPhotoFieldName(photoType);

            setProject(prev => {
                if (!prev || !prev[fieldName]) return prev;
                const { [key]: _, ...remainingPhotos } = prev[fieldName] as Record<string, unknown>;
                return {
                    ...prev,
                    [fieldName]: remainingPhotos,
                };
            });
        } catch (err) {
            console.error('Error deleting photo:', err);
            fail(err, 'Kunde inte ta bort bild');
        } finally {
            setSubmitting(false);
        }
    }, [token, task, fail]);

    // ============================================================================
    // Attachments
    // ============================================================================

    const uploadAttachment = useCallback(async (file: File) => {
        if (!token || !externalUserId) return;
        setSubmitting(true);
        setError(null);

        try {
            // Get upload URL and storage path (no photoType = attachment)
            const { uploadUrl, key, storagePath } = await guestApi.getUploadUrl(token, task, file.name, file.type);

            // Upload file directly to signed URL
            const uploadResponse = await fetch(uploadUrl, {
                method: 'PUT',
                body: file,
                headers: {
                    'Content-Type': file.type,
                },
            });

            if (!uploadResponse.ok) {
                const errorText = await uploadResponse.text();
                console.error('Storage upload failed:', uploadResponse.status, errorText);
                throw new Error(`Upload failed: ${uploadResponse.status}`);
            }

            // Register upload with server (include fileName and mimeType for proper attachment schema)
            const isImage = file.type.startsWith('image/');
            const dimensions = isImage ? await getImageDimensions(file).catch(() => ({ width: 0, height: 0 })) : { width: 0, height: 0 };
            await guestApi.registerUpload(token, task, {
                key,
                storagePath,
                width: dimensions.width,
                height: dimensions.height,
                fileSize: file.size,
                // no photoType for attachments
                fileName: file.name,
                mimeType: file.type,
            });

            // Update local state
            const now = formatDateForStorage(new Date());
            const fileExtension = file.name.split('.').pop() || '';

            setProject(prev => {
                if (!prev) return prev;
                const existingAttachments = prev.attachments || {};

                const newAttachment: FileAttachment | ImageAttachment = isImage
                    ? {
                        attachmentType: 'image' as const,
                        key,
                        storagePath,
                        fileSize: file.size,
                        author: externalUserId,
                        date: now,
                        width: dimensions.width,
                        height: dimensions.height,
                        fileName: file.name,
                        mimeType: file.type,
                    }
                    : {
                        attachmentType: 'file' as const,
                        key,
                        storagePath,
                        fileName: file.name,
                        fileType: fileExtension,
                        mimeType: file.type,
                        fileSize: file.size,
                        uploadedBy: externalUserId,
                        uploadedAt: now,
                    };

                return {
                    ...prev,
                    attachments: {
                        ...existingAttachments,
                        [key]: newAttachment,
                    },
                };
            });
        } catch (err) {
            console.error('Error uploading attachment:', err);
            parkOnRefusal(err);
            throw err; // Re-throw so component can handle locally
        } finally {
            setSubmitting(false);
        }
    }, [token, externalUserId, task, parkOnRefusal]);

    const deleteAttachment = useCallback(async (attachmentKey: string) => {
        if (!token) {
            setError('Sessionen är inte aktiv');
            return;
        }
        setSubmitting(true);
        setError(null);

        try {
            await guestApi.deleteAttachment(token, task, attachmentKey);

            // Update local state
            setProject(prev => {
                if (!prev || !prev.attachments) return prev;
                const { [attachmentKey]: _, ...remainingAttachments } = prev.attachments;
                return {
                    ...prev,
                    attachments: remainingAttachments,
                };
            });
        } catch (err) {
            console.error('Error deleting attachment:', err);
            fail(err, 'Kunde inte ta bort fil');
        } finally {
            setSubmitting(false);
        }
    }, [token, task, fail]);

    const getDownloadUrl = useCallback(async (storagePath: string): Promise<string> => {
        if (!token) throw new Error('Ingen åtkomsttoken');
        return guestApi.getDownloadUrl(token, task, storagePath);
    }, [token, task]);

    return {
        loading,
        error,
        refusal,
        taskType,
        project,
        building,
        externalUserId,
        submitting,
        uploadingPhotoType,
        completedAt,
        completedNote,
        workOrderContext,
        workOrderCompleted,
        refetch: fetchProject,
        clearError,
        setCompletion,
        completeWorkOrder,
        addNote,
        updateNote,
        deleteNote,
        createChecklist,
        addChecklistItem,
        setChecklistItemCompleted,
        deleteChecklistItem,
        deleteChecklist,
        uploadPhoto,
        deletePhoto,
        uploadAttachment,
        deleteAttachment,
        getDownloadUrl,
        canDelete,
    };
};

/**
 * Get image dimensions from a File
 */
export function getImageDimensions(file: File): Promise<{ width: number; height: number }> {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => {
            resolve({ width: img.width, height: img.height });
            URL.revokeObjectURL(img.src);
        };
        img.onerror = () => {
            reject(new Error('Could not load image'));
            URL.revokeObjectURL(img.src);
        };
        img.src = URL.createObjectURL(file);
    });
}
